import React, { useState } from 'react';
import { Button, Card, Col, Form, Row } from '@themesberg/react-bootstrap';
import PageHeader, { AlertMessage } from '../../components/PageHeader';
import { authApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

export default function AccountSettings() {
  const { tenantId, tenantName, clientSecret, logout, updateTenantName } = useAuth();
  const [name, setName] = useState(tenantName);
  const [newSecret, setNewSecret] = useState('');
  const [revokeTokens, setRevokeTokens] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSaveName = async () => {
    setError('');
    setSuccess('');
    setLoading(true);
    try {
      await updateTenantName(name.trim());
      setSuccess('Workspace name updated');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRotate = async () => {
    setError('');
    setSuccess('');
    setLoading(true);
    try {
      const { data } = await authApi.rotateSecret(revokeTokens);
      setNewSecret(data.clientSecret);
      setSuccess(revokeTokens ? 'Secret rotated and outstanding JWTs revoked' : 'Secret rotated');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRevoke = async () => {
    if (!window.confirm('Suspend tenant and invalidate all JWTs?')) return;
    setError('');
    setSuccess('');
    setLoading(true);
    try {
      await authApi.revokeSecret();
      setSuccess('Tenant suspended. You will be signed out.');
      setTimeout(logout, 1500);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const copy = (text) => navigator.clipboard.writeText(text);

  return (
    <>
      <PageHeader title="Account" subtitle="Manage tenant credentials and client secret lifecycle." />

      <AlertMessage variant="danger" message={error} onClose={() => setError('')} />
      <AlertMessage variant="success" message={success} onClose={() => setSuccess('')} />

      <Row>
        <Col lg={6} className="mb-4">
          <Card border="light" className="shadow-sm">
            <Card.Body>
              <h5 className="mb-3">Workspace</h5>
              <Form.Group className="mb-3">
                <Form.Label>Name</Form.Label>
                <div className="d-flex gap-2">
                  <Form.Control
                    value={name}
                    maxLength={128}
                    onChange={(e) => setName(e.target.value)}
                  />
                  <Button
                    variant="outline-primary"
                    onClick={handleSaveName}
                    disabled={loading || !name.trim() || name.trim() === tenantName}
                  >
                    Save
                  </Button>
                </div>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Tenant ID</Form.Label>
                <Form.Control readOnly value={tenantId} />
              </Form.Group>
              <Form.Group className="mb-0">
                <Form.Label>Stored client secret</Form.Label>
                <Form.Control readOnly type="password" value={clientSecret} />
                <Form.Text className="text-muted">Used locally for re-login only.</Form.Text>
              </Form.Group>
            </Card.Body>
          </Card>
        </Col>
        <Col lg={6} className="mb-4">
          <Card border="light" className="shadow-sm">
            <Card.Body>
              <h5 className="mb-3">Rotate client secret</h5>
              <Form.Check
                type="checkbox"
                id="revokeTokens"
                className="mb-3"
                label="Also revoke outstanding JWTs"
                checked={revokeTokens}
                onChange={(e) => setRevokeTokens(e.target.checked)}
              />
              <Button variant="primary" onClick={handleRotate} disabled={loading}>
                Rotate secret
              </Button>
              {newSecret ? (
                <div className="mt-4">
                  <Form.Label>New client secret (shown once)</Form.Label>
                  <div className="d-flex gap-2">
                    <Form.Control readOnly value={newSecret} />
                    <Button variant="outline-primary" onClick={() => copy(newSecret)}>Copy</Button>
                  </div>
                </div>
              ) : null}
            </Card.Body>
          </Card>
        </Col>
        <Col lg={6} className="mb-4">
          <Card border="light" className="shadow-sm border-danger">
            <Card.Body>
              <h5 className="mb-3 text-danger">Danger zone</h5>
              <p className="text-gray">
                Revoking the client secret suspends the tenant and invalidates every outstanding JWT.
              </p>
              <Button variant="danger" onClick={handleRevoke} disabled={loading}>
                Revoke secret & suspend
              </Button>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  );
}
